<div class="nurse-attendance-container" [style.--col-width.px]="columnWidthPx"
  [class.roster-compact]="columnWidthPx < 50">
  <!-- Sticky Header Section -->
  <div class="sticky-header">
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-bold">Nurse Attendance</h2>

      <div class="col-width-slider flex items-center gap-2">
        <span class="col-width-label text-xs text-gray-400">20px</span>
        <input type="range" min="20" max="200" step="10" [(ngModel)]="columnWidthPx"
          (ngModelChange)="onColumnWidthChange($event)" [attr.aria-label]="'Column width'" title="Column width"
          class="cursor-pointer" />
        <span class="col-width-label text-xs text-gray-400">200px</span>
      </div>
    </div>
    <div class="flex flex-row items-center gap-3">
      <form class="w-64" [formGroup]="dateForm">
        <tui-input-date-range tuiTextfieldSize="s" formControlName="selectedRange"
          tuiUnfinishedValidator="Complete the date selection">
          Choose dates
          <input placeholder="From - To" tuiTextfieldLegacy />
        </tui-input-date-range>
      </form>

      <tui-input class="w-60" tuiTextfieldSize="s" [formControl]="searchControl" [tuiTextfieldCleaner]="true">
        Search Nurse Name
        <input tuiTextfieldLegacy placeholder="" />
      </tui-input>

      <button (click)="fetchNurseAttendance()" appearance="secondary" tuiButton type="button" size="s"
        style="border: 2px solid #e5e7ea; font-weight: 800">
        Search
      </button>
      <button (click)="reset()" appearance="flat" iconStart="@tui.rotate-cw" tuiButton type="button" size="s">
        Clear
      </button>
    </div>
  </div>


  <!-- Nurse Attendance Table -->
  <tui-loader size="xl" [overlay]="true" [showLoader]="loading">
    <div class="table-container mt-2" *ngIf="filteredAttendanceData.length > 0 && !loading">
      <table class="attendance-table attendance-table-compact">
        <thead>
          <tr>
            <th class="fixed-left-seq">#</th>
            <th class="fixed-left client-col-header">Nurse Name</th>
            <th *ngFor="let date of dateRange" class="date-column text-left"
              [attr.title]="date | date: 'EEEE, MMMM d, yyyy'">
              <span class="day-number font-bold text-xs">{{ date | date: "d" }}</span>
              <span class="day-weekday text-[9px] block font-semibold uppercase"
                [class.text-[#ef4444]]="date.getDay() === 0 || date.getDay() === 6"
                [class.text-gray-400]="date.getDay() !== 0 && date.getDay() !== 6">
                {{ date | date: (columnWidthPx >= 100 ? "EEEE" : columnWidthPx >= 60 ? "EEE" : "EEEEE") }}
              </span>
              <span class="day-month text-[9px] block font-normal text-gray-500">
                {{ date | date: (columnWidthPx >= 100 ? "MMMM" : "MMM") }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let nurse of filteredAttendanceData; let i = index">
            <td class="fixed-left-seq text-center">{{ i + 1 }}</td>
            <td class="fixed-left client-cell">
              <div class="client-id font-bold text-xs">{{ nurse.nurserefNo }}</div>
              <div class="client-name-line text-[11px] font-semibold text-gray-600 truncate mt-[2px]"
                [attr.title]="nurse.nurseName">
                {{ nurse.nurseName }}
              </div>
              <div class="client-charge-line text-[11px] font-bold text-[#526ed3] mt-[2px]">
                Total: Rs. {{ nurse.totalSalary | number: "1.0-0" }}
              </div>
            </td>

            <!-- Loop through each date in the selected date range -->
            <td *ngFor="let date of dateRange" class="date-cell"
              [class.off-day-cell]="hasOffDayForDate(nurse.shifts, date, nurse._id)">
              <div *ngFor="let shift of getShiftsForDate(nurse.shifts, date, nurse._id)">
                <!-- Off Day / Leave Display -->
                <div *ngIf="shift.isOffDay" class="off-day-card max-w-60" [class.leave-day-card]="shift.isLeave"
                  [attr.title]="getShiftTooltip(shift)">
                  <div class="off-day-header">{{ shift.isLeave ? (shift.leaveType === 'AnnualLeave' ? 'Annual Leave' :
                    'Day-Off') : 'Off Day' }}</div>
                  <span class="compact-letter hidden">-</span>
                </div>

                <!-- Regular Shift Display -->
                <div *ngIf="!shift.isOffDay" class="shift-card cursor-pointer max-w-60"
                  [attr.title]="getShiftTooltip(shift)" (click)="showDialog(shift._id)">
                  <span class="compact-letter hidden">{{ (shift.clientName || 'U').charAt(0) }}</span>
                  <div class="shift-header">{{ shift.clientName }}</div>
                  <div class="shift-details">
                    <div class="shift-nurse">Client Reg No: {{ shiftClientDisplayRef(shift) }}</div>
                    <div class="shift-nurse">Shift: {{ shift.shiftType }}</div>
                    <div class="shift-nurse">Meal: {{ shift.mealOption }}</div>
                    <div class="shift-nurse">Roster: {{ shift.roster }}</div>

                    <div class="shift-nurse">Salary: {{ shift.salary | currency : 'Rs ' }}</div>
                    <div class="shift-nurse">
                      Additional Notes: {{ shift.notes }}
                    </div>
                    <div class="text-[10px] text-gray-400 mt-1">
                      {{ shift.date | date: "longDate" }}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>


    <!-- No Data Message -->
    <div *ngIf="!loading && filteredAttendanceData.length === 0" class="text-center text-gray-500">
      No data available for the selected date range.
    </div>
  </tui-loader>
</div>


<!-- Single Update Shift Dialog Template -->
<ng-template let-observer [tuiDialogOptions]="{ label: 'Update Shift', size: 'm' }" [(tuiDialog)]="open">
  <tui-loader [overlay]="true" [showLoader]="loading">
    <form class="mt-3" [formGroup]="shiftForm" (ngSubmit)="updateShift()">

      <tui-combo-box formControlName="nurse" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyNurses"
        placeholder="Select Nurse">Select Nurse
        <tui-data-list-wrapper *tuiDataList [items]="nurses | tuiFilterByInput"
          [itemContent]="stringifyNurses | tuiStringifyContent">
        </tui-data-list-wrapper>
      </tui-combo-box>
      <tui-error class="mb-3" formControlName="nurse" [error]="[] | tuiFieldError | async" />

      <tui-combo-box formControlName="client" [tuiTextfieldLabelOutside]="true" [stringify]="stringifyClients"
        placeholder="Select Client">Select Client
        <tui-data-list-wrapper *tuiDataList [items]="clients | tuiFilterByInput"
          [itemContent]="stringifyClients | tuiStringifyContent">
        </tui-data-list-wrapper>
      </tui-combo-box>
      <tui-error class="mb-3" [error]="[] | tuiFieldError | async" />

      <tui-combo-box [stringify]="stringifyDropdown" formControlName="roster">
        Select Roster
        <input tuiTextfieldLegacy placeholder="Select Roster" />
        <tui-data-list-wrapper *tuiDataList [items]="rosterItems"
          [itemContent]="stringifyDropdown | tuiStringifyContent" />
      </tui-combo-box>
      <tui-error formControlName="roster" class="mb-3" [error]="[] | tuiFieldError | async" />

      <div *ngIf="shiftForm.get('roster')?.value?.value === 12">
        <tui-combo-box [stringify]="stringifyDropdown" formControlName="rosterType">
          Select Roster Type
          <input tuiTextfieldLegacy placeholder="Select Roster type" />
          <tui-data-list-wrapper *tuiDataList [items]="rosterType"
            [itemContent]="stringifyDropdown | tuiStringifyContent" />
        </tui-combo-box>
        <tui-error formControlName="rosterType" class="mb-3" [error]="[] | tuiFieldError | async" />
      </div>

      <tui-combo-box [stringify]="stringifyDropdown" formControlName="mealOption">
        Select Meal Option
        <input tuiTextfieldLegacy placeholder="Select Roster" />
        <tui-data-list-wrapper *tuiDataList [items]="mealOptions"
          [itemContent]="stringifyDropdown | tuiStringifyContent" />
      </tui-combo-box>
      <tui-error formControlName="mealOption" class="mb-3" [error]="[] | tuiFieldError | async" />

      <tui-combo-box [stringify]="stringifyDropdown" formControlName="typeOption" class="mb-3">
        Select Type
        <input tuiTextfieldLegacy placeholder="Select Type" />
        <tui-data-list-wrapper *tuiDataList [items]="typeOptions"
          [itemContent]="stringifyDropdown | tuiStringifyContent" />
      </tui-combo-box>
      <tui-error formControlName="typeOption" class="mb-3" [error]="[] | tuiFieldError | async" />

      <div class="w-full">
        <label tuiLabel>
          <tui-textarea formControlName="address" [tuiTextfieldLabelOutside]="true" placeholder="Enter Address"
            title="Textarea Input">
            Address
          </tui-textarea>
          <tui-error formControlName="address" class="mb-3" [error]="[] | tuiFieldError | async" />
        </label>
      </div>

      <tui-input formControlName="charge">Charge <input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="charge" class="mb-3" [error]="[] | tuiFieldError | async" />

      <tui-input formControlName="salary">Salary <input tuiTextfieldLegacy type="text" /></tui-input>
      <tui-error formControlName="salary" class="mb-3" [error]="[] | tuiFieldError | async" />

      <div>
        <tui-input-date formControlName="startDate" [tuiTextfieldLabelOutside]="true"
          placeholder="Select Start Date & Time">
          Select Start Date
        </tui-input-date>
        <tui-error formControlName="startDate" class="mb-3" [error]="[] | tuiFieldError | async"></tui-error>
      </div>

      <div class="w-full">
        <label tuiLabel>
          <tui-textarea formControlName="notes" [tuiTextfieldLabelOutside]="true" placeholder="Enter notes"
            title="Textarea Input">
            Additional Notes
          </tui-textarea>
          <tui-error formControlName="notes" class="mb-3" [error]="[] | tuiFieldError | async" />
        </label>
      </div>

      <div class="flex justify-between items-center">
        <button tuiButton type="button" appearance="danger" (click)="deleteShift()">
          Delete shift
        </button>
        <button tuiButton type="submit">Update shift</button>
      </div>
    </form>
  </tui-loader>
</ng-template>